// frontend/app/components/discoveryJourneys/DiscoveryJourneyCard.js
//
// One journey on an index or a dashboard. The compelling question is the
// headline — that is the title the student sees in place of "Chapter 2: Motion
// and Kinematics" — the mission set-up sits under it, and the chain of loops is
// the last thing read before the call to action.
//
// Every word on this card came out of the payload. Nothing about any particular
// journey is known here.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { tint } from "../lessonPlans/lessonPlanTheme";
import { fill } from "./journeyProgress";

const DOTS_MAX = 10; // past this the loop dots stop being countable at a glance

const breadcrumbOf = (journey) =>
  [journey?.subject_name, journey?.chapter_name].filter(Boolean).join("  ›  ");

/**
 * The chain of loops as dots, so the length of the journey and the student's
 * place in it are one glance rather than two sentences.
 */
export const JourneyLoopRail = ({ journey, progress, colors, accent, t }) => {
  const { total, completed, current, isComplete } = progress;
  if (!total) return null;

  // A loop not reached yet is drawn in the theme's strong border, so an unlit
  // dot has a dark-mode counterpart without a hex living in a component.
  const upcoming = colors.borderStrong;
  const doneColor = isComplete ? colors.success : accent;

  const caption = isComplete
    ? t("Journey complete")
    : current
      ? fill(t("Loop {current} of {total}"), { current, total })
      : fill(t("{total} learning loops"), { total });

  const captionColor = isComplete
    ? colors.success
    : completed > 0 || current > 1
      ? colors.textSecondary
      : colors.textMuted;

  const dotColor = (index) => {
    if (index < completed) return doneColor;
    if (current && index === current - 1) return accent;
    return upcoming;
  };

  const donePct = Math.round((completed / total) * 100);

  return (
    <View testID={`journey-loops-${journey.id}`} style={styles.railRow}>
      {total > DOTS_MAX ? (
        <View style={[styles.meterTrack, { backgroundColor: upcoming }]}>
          <View
            style={[styles.meterFill, { width: `${donePct}%`, backgroundColor: doneColor }]}
          />
        </View>
      ) : (
        Array.from({ length: total }, (_, index) => (
          <View
            key={index}
            style={[
              current && index === current - 1 ? styles.dotCurrent : styles.dot,
              { backgroundColor: dotColor(index) },
            ]}
          />
        ))
      )}
      {isComplete ? (
        <MaterialCommunityIcons
          name="check-circle"
          size={13}
          color={colors.success}
          style={styles.railIcon}
        />
      ) : null}
      <Text
        style={[
          styles.railText,
          isComplete && styles.railTextAfterIcon,
          { color: captionColor },
        ]}
      >
        {caption}
      </Text>
    </View>
  );
};

const DiscoveryJourneyCard = ({ journey, progress, colors, isDarkMode, onOpen, onEdit, t }) => {
  const accent = colors.accentPrimary;
  const objectives = Array.isArray(journey.objectives) ? journey.objectives : [];
  const mission = String(journey.subtitle || journey.mission_story || "").trim();
  const breadcrumb = breadcrumbOf(journey);
  const { isComplete, total } = progress;

  return (
    <TouchableOpacity
      onPress={() => onOpen && onOpen(journey)}
      accessibilityRole="button"
      accessibilityLabel={`${t("Discovery Journey")}: ${journey.title}`}
      style={[
        styles.card,
        {
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
          shadowColor: colors.shadow,
          shadowOpacity: colors.shadowOpacity,
        },
      ]}
    >
      <View
        style={[styles.cardStripe, { backgroundColor: isComplete ? colors.success : accent }]}
      />

      <View style={styles.cardHeader}>
        <View style={[styles.eyebrowIcon, { backgroundColor: tint(accent, isDarkMode, 2.4) }]}>
          <MaterialCommunityIcons name="map-marker-path" size={16} color={accent} />
        </View>
        <View style={styles.cardHeaderText}>
          <Text style={[styles.eyebrow, { color: accent }]}>{t("Discovery Journey")}</Text>
          {breadcrumb ? (
            <Text style={[styles.breadcrumb, { color: colors.textMuted }]} numberOfLines={1}>
              {breadcrumb}
            </Text>
          ) : null}
        </View>
        {journey.status === "draft" ? (
          <View
            style={[styles.statusBadge, { backgroundColor: tint(colors.warning, isDarkMode, 2.4) }]}
          >
            <MaterialCommunityIcons name="pencil-outline" size={12} color={colors.warning} />
            <Text style={[styles.statusText, { color: colors.warning }]}>{t("Draft")}</Text>
          </View>
        ) : null}
      </View>

      {/* The compelling question is the headline. Everything else supports it. */}
      <Text style={[styles.question, { color: colors.textPrimary }]} numberOfLines={3}>
        {journey.title}
      </Text>

      <View style={styles.cardBody}>
        {mission ? (
          <Text style={[styles.mission, { color: colors.textSecondary }]} numberOfLines={3}>
            {mission}
          </Text>
        ) : null}

        <View style={styles.cardMeta}>
          {total ? (
            <View style={styles.metaItem}>
              <MaterialCommunityIcons
                name="rotate-3d-variant"
                size={13}
                color={colors.textMuted}
              />
              <Text style={[styles.metaText, { color: colors.textMuted }]}>
                {fill(t(total === 1 ? "{total} loop" : "{total} loops"), { total })}
              </Text>
            </View>
          ) : null}
          {journey.grade_label ? (
            <View style={[styles.pill, { backgroundColor: tint(accent, isDarkMode, 1.6) }]}>
              <Text style={[styles.pillText, { color: accent }]}>{journey.grade_label}</Text>
            </View>
          ) : null}
          {objectives.length > 0 ? (
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="target" size={13} color={colors.textMuted} />
              <Text style={[styles.metaText, { color: colors.textMuted }]}>
                {fill(
                  t(objectives.length === 1 ? "{count} objective" : "{count} objectives"),
                  { count: objectives.length }
                )}
              </Text>
            </View>
          ) : null}
        </View>

        <JourneyLoopRail
          journey={journey}
          progress={progress}
          colors={colors}
          accent={accent}
          t={t}
        />
      </View>

      <View style={[styles.cardCta, { borderTopColor: colors.border }]}>
        {onEdit ? (
          <TouchableOpacity
            onPress={() => onEdit(journey)}
            accessibilityRole="button"
            accessibilityLabel={`${t("Edit")} ${journey.title}`}
            style={styles.editButton}
          >
            <MaterialCommunityIcons name="pencil-outline" size={14} color={colors.textMuted} />
            <Text style={[styles.editText, { color: colors.textMuted }]}>{t("Edit")}</Text>
          </TouchableOpacity>
        ) : null}
        <Text style={[styles.ctaText, { color: isComplete ? colors.success : accent }]}>
          {isComplete
            ? t("Review journey")
            : progress.current > 1 || progress.completed > 0
              ? t("Continue journey")
              : t("Begin journey")}
        </Text>
        <MaterialCommunityIcons
          name="arrow-right"
          size={15}
          color={isComplete ? colors.success : accent}
        />
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    flex: 1,
    borderRadius: 14,
    borderWidth: 1,
    overflow: "hidden",
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 3 },
    elevation: 2,
  },
  cardStripe: { height: 3, width: "100%" },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    padding: 14,
    paddingBottom: 6,
    gap: 10,
  },
  eyebrowIcon: {
    width: 30,
    height: 30,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  cardHeaderText: { flex: 1, minWidth: 0 },
  eyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  breadcrumb: { fontSize: 11, marginTop: 2 },
  question: {
    fontSize: 18,
    fontWeight: "800",
    lineHeight: 24,
    paddingHorizontal: 14,
    paddingTop: 2,
  },
  cardBody: { flex: 1, paddingTop: 8 },
  mission: { fontSize: 13, lineHeight: 19, paddingHorizontal: 14, paddingBottom: 10 },
  cardMeta: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    paddingHorizontal: 14,
    paddingBottom: 12,
    gap: 10,
  },
  metaItem: { flexDirection: "row", alignItems: "center", gap: 4 },
  metaText: { fontSize: 11, fontWeight: "600", marginLeft: 4 },
  pill: { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 },
  pillText: { fontSize: 10, fontWeight: "800", letterSpacing: 0.3 },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
    gap: 4,
  },
  statusText: { fontSize: 10, fontWeight: "800", marginLeft: 4 },

  railRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 14,
    paddingBottom: 12,
  },
  dot: { width: 6, height: 6, borderRadius: 3 },
  dotCurrent: { width: 14, height: 6, borderRadius: 3 },
  railIcon: { marginLeft: 8 },
  railText: { fontSize: 11, fontWeight: "700", marginLeft: 8 },
  // The tick already carries the separation from the dots; a second one would
  // push the word away from its own icon.
  railTextAfterIcon: { marginLeft: 0 },
  meterTrack: { width: 96, height: 6, borderRadius: 999, overflow: "hidden" },
  meterFill: { height: "100%", borderRadius: 999 },

  cardCta: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  ctaText: { fontSize: 12, fontWeight: "700" },
  editButton: { flexDirection: "row", alignItems: "center", gap: 4, marginRight: "auto" },
  editText: { fontSize: 11, fontWeight: "600" },
});

export default DiscoveryJourneyCard;
